import React, { useState, useEffect } from 'react';
import {
    Calendar, Clock, MapPin, Ticket, ArrowLeft, ChevronRight, ChevronLeft, Video, Image
} from 'lucide-react';
import { API_URL, fetchData } from '../helpers/api';
import FormatDate, { isMidnight } from '../utils/dateTools';
import { useParams } from 'react-router-dom';
import TruncatableHtmlContent from '../utils/TruncatableHtmlContent';

const SingleEventPage = () => {
    const { eventId } = useParams();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedEvents, setRelatedEvents] = useState([]);
    const [isUsingLinkedEvents, setIsUsingLinkedEvents] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
    const [hasEventPassed, setHasEventPassed] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);
    const [activeTab, setActiveTab] = useState('images'); // 'images' or 'video'
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchEventData = async () => {
            try {
                setLoading(true);
                
                // 1. Fetch current event data with deeply populated linked and related events
                const eventData = await fetchData(`/api/events/${eventId}`, {
                    populate: {
                        images: true,
                        video: true,
                        linkedEvents: {
                            populate: {
                                images: true,
                                linkedEvents: {
                                    populate: {
                                        images: true
                                    }
                                },
                                relatedEvents: {
                                    populate: {
                                        images: true
                                    }
                                }
                            }
                        },
                        relatedEvents: {
                            populate: {
                                images: true,
                                linkedEvents: {
                                    populate: {
                                        images: true
                                    }
                                },
                                relatedEvents: {
                                    populate: {
                                        images: true
                                    }
                                }
                            }
                        }
                    }
                });
                
                // Check if event is published
                if (!eventData.isPublished) {
                    setError('Cet événement n\'est pas encore publié.');
                    setLoading(false);
                    return;
                }
                
                setEvent(eventData);
                
                // 2. Media and date settings
                if (eventData.video && eventData.video.url && (!eventData.images || eventData.images.length === 0)) {
                    setActiveTab('video');
                }
                
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const eventEndDate = eventData.newEndDate ? new Date(eventData.newEndDate) : new Date(eventData.newStartDate);
                eventEndDate.setHours(23, 59, 59, 999);
                setHasEventPassed(eventEndDate < today);
                
                if (eventData.video && eventData.video.url) {
                    try {
                        const videoLink = API_URL + eventData.video.url;
                        setVideoUrl(videoLink);
                    } catch (error) {
                        console.error('Error fetching video link:', error);
                    }
                }
                
                // 3. Create a network of events using a Map to track all connected events
                // This will help identify all events in the network, regardless of direct connection
                const networkMap = new Map();
                
                // Set to keep track of all event IDs related to current event
                const relatedEventIds = new Set();
                
                // Helper function to add event to networkMap
                const addEventToNetwork = (event) => {
                    if (!event || networkMap.has(event.id) || event.id === eventData.id) return;
                    
                    networkMap.set(event.id, {
                        id: event.id,
                        documentId: event.documentId,
                        title: event.title,
                        newStartDate: event.newStartDate,
                        newEndDate: event.newEndDate,
                        images: event.images?.map(img => ({
                            url: img.url
                        })) || []
                    });
                    
                    relatedEventIds.add(event.id);
                };
                
                // Step 1: Add first-degree connections (direct connections)
                // console.log('Linked events:', eventData.linkedEvents);
                // console.log('Related events:', eventData.relatedEvents);
                
                // Add events that this event links to (linkedEvents)
                if (eventData.linkedEvents && eventData.linkedEvents.length > 0) {
                    eventData.linkedEvents.forEach(linkedEvent => {
                        addEventToNetwork(linkedEvent);
                        
                        // Step 2: Add second-degree connections from linkedEvents
                        // Events that are linked to events that this event links to
                        if (linkedEvent.linkedEvents && linkedEvent.linkedEvents.length > 0) {
                            linkedEvent.linkedEvents.forEach(secondDegreeEvent => {
                                addEventToNetwork(secondDegreeEvent);
                            });
                        }
                        
                        // Events that link to events that this event links to
                        if (linkedEvent.relatedEvents && linkedEvent.relatedEvents.length > 0) {
                            linkedEvent.relatedEvents.forEach(secondDegreeEvent => {
                                addEventToNetwork(secondDegreeEvent);
                            });
                        }
                    });
                }
                
                // Add events that link to this event (relatedEvents)
                if (eventData.relatedEvents && eventData.relatedEvents.length > 0) {
                    eventData.relatedEvents.forEach(relatedEvent => {
                        addEventToNetwork(relatedEvent);
                        
                        // Step 2: Add second-degree connections from relatedEvents
                        // Events that are linked to events that link to this event
                        if (relatedEvent.linkedEvents && relatedEvent.linkedEvents.length > 0) {
                            relatedEvent.linkedEvents.forEach(secondDegreeEvent => {
                                addEventToNetwork(secondDegreeEvent);
                            });
                        }
                        
                        // Events that link to events that link to this event
                        if (relatedEvent.relatedEvents && relatedEvent.relatedEvents.length > 0) {
                            relatedEvent.relatedEvents.forEach(secondDegreeEvent => {
                                addEventToNetwork(secondDegreeEvent);
                            });
                        }
                    });
                }
                
                // Convert Map values to array for related events
                const networkEvents = Array.from(networkMap.values());
                
                // If we have network events, use them
                if (networkEvents.length > 0) {
                    // console.log('Network of related events:', networkEvents);
                    setRelatedEvents(networkEvents);
                    setIsUsingLinkedEvents(true);
                } else {
                    // 4. If no linked events, fetch random similar events
                    const allEvents = await fetchData('/api/events?filters[isPublished][$eq]=true', {
                        populate: ['images']
                    });
                    
                    // Filter out the current event
                    const otherEvents = allEvents.filter(e => e.id !== eventData.id);
                    
                    // Shuffle the events array
                    const shuffledEvents = otherEvents.sort(() => 0.5 - Math.random());
                    
                    // Pick the first 3 events
                    const randomEvents = shuffledEvents.slice(0, 3);
                    
                    setRelatedEvents(randomEvents);
                    setIsUsingLinkedEvents(false);
                }
            } catch (error) {
                console.error('Error fetching event data:', error);
                setError('Une erreur est survenue lors du chargement de l\'événement.');
            } finally {
                setLoading(false);
            }
        };

        fetchEventData();
    }, [eventId]);

    // Image viewer navigation functions
    const openImageViewer = (index) => {
        setSelectedImageIndex(index);
        setIsImageViewerOpen(true);
    };

    const navigateImage = (direction) => {
        if (!event || !event.images) return;

        const totalImages = event.images.length;
        let newIndex = selectedImageIndex;

        if (direction === 'next') {
            newIndex = (selectedImageIndex + 1) % totalImages;
        } else {
            newIndex = (selectedImageIndex - 1 + totalImages) % totalImages;
        }

        setSelectedImageIndex(newIndex);
    };

    // Function to check if media exists
    const hasImages = event?.images && event.images.length > 0;
    const hasVideo = !!videoUrl;

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-warning" role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
            </div>
        );
    }

    if (!event || error) {
        return (
            <div className="container py-5 text-center">
                <h1>Événement non disponible</h1>
                <p>{error || 'Désolé, l\'événement que vous recherchez n\'existe pas.'}</p>
                <a href="/evenements" className="btn btn-primary mt-3">
                    Retour aux événements
                </a>
            </div>
        );
    }

    return (
        <div className="single-event">
            {/* Header with Back Button */}
            <div className="container py-4 mt-4">
                <a
                    href="/evenements"
                    className="btn btn-outline-dark rounded-pill mb-4 d-inline-flex align-items-center"
                >
                    <ArrowLeft size={20} className="me-2" />
                    Retour aux événements
                </a>
            </div>

            {/* Image Viewer Modal */}
            {hasImages && (
                <div
                    className={`modal fade ${isImageViewerOpen ? 'show d-block' : ''}`}
                    tabIndex="-1"
                    style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 2000 }}
                >
                    <div className="modal-dialog modal-fullscreen">
                        <div className="modal-content bg-transparent border-0">
                            <div className="modal-header border-0">
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setIsImageViewerOpen(false)}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body d-flex justify-content-center align-items-center position-relative">
                                {event.images.length > 1 && (
                                    <button
                                        className="btn btn-link text-white position-absolute start-0 top-50 translate-middle-y"
                                        onClick={() => navigateImage('prev')}
                                    >
                                        <ChevronLeft size={48} />
                                    </button>
                                )}
                                <img
                                    src={API_URL + event?.images[selectedImageIndex]?.url}
                                    alt={`${event.title} - Image ${selectedImageIndex + 1}`}
                                    className="img-fluid max-vh-90"
                                    style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
                                />
                                {event.images.length > 1 && (
                                    <button
                                        className="btn btn-link text-white position-absolute end-0 top-50 translate-middle-y"
                                        onClick={() => navigateImage('next')}
                                    >
                                        <ChevronRight size={48} />
                                    </button>
                                )}
                            </div>
                            <div className="modal-footer border-0 justify-content-center text-white">
                                <span>{`${selectedImageIndex + 1} / ${event.images.length}`}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Event Hero Section */}
            <div className="container">
                <div className="row gx-5 py-5">
                    {/* Media Gallery - Images and Video */}
                    <div className="col-lg-7 mb-4 mb-lg-0 position-sticky-container">
                        <div className="position-sticky" style={{ top: '100px', zIndex: 10 }}>
                            {/* Media Tab Navigation - Only show if both video and images exist */}
                            {hasVideo && hasImages && (
                                <ul className="nav nav-tabs mb-3">
                                    <li className="nav-item">
                                        <button 
                                            className={`nav-link ${activeTab === 'images' ? 'active fw-bold' : ''}`} 
                                            onClick={() => setActiveTab('images')}
                                        >
                                            <Image size={18} className="me-2" />
                                            Photos ({event.images.length})
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button 
                                            className={`nav-link ${activeTab === 'video' ? 'active fw-bold' : ''}`} 
                                            onClick={() => setActiveTab('video')}
                                        >
                                            <Video size={18} className="me-2" />
                                            Vidéo
                                        </button>
                                    </li>
                                </ul>
                            )}

                            {/* Media Content Based on Active Tab */}
                            {(!hasVideo && !hasImages) ? (
                                <div className="bg-light rounded d-flex justify-content-center align-items-center" style={{ height: '500px' }}>
                                    <p className="text-muted">Pas d'image ou de vidéo disponible</p>
                                </div>
                            ) : (
                                <>
                                    {/* Images Tab Content */}
                                    {(activeTab === 'images' && hasImages) && (
                                        <div
                                            id="eventImageCarousel"
                                            className="carousel slide rounded shadow-lg overflow-hidden"
                                            data-bs-ride="carousel"
                                        >
                                            {/* Indicators */}
                                            {event.images.length > 1 && (
                                                <div className="carousel-indicators">
                                                    {event.images.map((_, index) => (
                                                        <button
                                                            key={index}
                                                            type="button"
                                                            data-bs-target="#eventImageCarousel"
                                                            data-bs-slide-to={index}
                                                            className={index === 0 ? "active" : ""}
                                                            aria-current={index === 0 ? "true" : "false"}
                                                            aria-label={`Slide ${index + 1}`}
                                                        ></button>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Carousel Inner */}
                                            <div className="carousel-inner">
                                                {event.images.map((image, index) => (
                                                    <div
                                                        key={index}
                                                        className={`carousel-item ${index === 0 ? 'active' : ''}`}
                                                        onClick={() => openImageViewer(index)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <img
                                                            src={API_URL + image.url}
                                                            alt={`${event.title} - Image ${index + 1}`}
                                                            className="d-block w-100"
                                                            style={{ objectFit: 'cover', maxHeight: '500px' }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Controls */}
                                            {event.images.length > 1 && (
                                                <>
                                                    <button
                                                        className="carousel-control-prev"
                                                        type="button"
                                                        data-bs-target="#eventImageCarousel"
                                                        data-bs-slide="prev"
                                                    >
                                                        <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                                        <span className="visually-hidden">Previous</span>
                                                    </button>
                                                    <button
                                                        className="carousel-control-next"
                                                        type="button"
                                                        data-bs-target="#eventImageCarousel"
                                                        data-bs-slide="next"
                                                    >
                                                        <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                                        <span className="visually-hidden">Next</span>
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* Video Tab Content */}
                                    {(activeTab === 'video' && hasVideo) && (
                                        <div className="rounded shadow-lg overflow-hidden">
                                            <div className="position-relative" style={{ paddingBottom: '56.25%', height: '0', overflow: 'hidden' }}>
                                                <video 
                                                    controls 
                                                    className="position-absolute top-0 start-0 w-100 h-100"
                                                    style={{ objectFit: 'cover' }}
                                                    poster={hasImages ? API_URL + event.images[0]?.url : null}
                                                >
                                                    <source src={videoUrl} type="video/mp4" />
                                                    Votre navigateur ne prend pas en charge la lecture de vidéos.
                                                </video>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Free Event Badge */}
                            {event.isFree && (
                                <div className="position-absolute top-0 end-0 m-3">
                                    <span className="badge bg-success rounded-pill px-3 py-2">
                                        Gratuit
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Event Details */}
                    <div className="col-lg-5">
                        <div>
                            <h1 className="display-6 fw-bold mb-4">{event.title}</h1>

                            <div className="d-flex flex-column gap-3 mb-4">
                                {/* Date */}
                                <div className="d-flex align-items-center">
                                    <Calendar size={24} className="text-warning me-3" />
                                    <div>
                                        <p className="mb-0 fw-semibold">
                                            {event.newEndDate ? (
                                                <>
                                                    Du <FormatDate dateString={event.newStartDate} format="long" />
                                                    {" "}au{" "}
                                                    <FormatDate dateString={event.newEndDate} format="long" />
                                                </>
                                            ) : (
                                                <FormatDate dateString={event.newStartDate} format="long" />
                                            )}
                                        </p>
                                        {!isMidnight(event.newStartDate) && (
                                            <p className="text-muted mb-0">
                                                <Clock size={16} className="me-1" />
                                                <FormatDate dateString={event.newStartDate} format="time" />
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Event Description */}
                                <div className="d-flex align-items-center">
                                    <div className="fs-5 text-muted">
                                        <TruncatableHtmlContent html={event.description} />
                                    </div>
                                </div>

                                {/* Ticket/Booking */}
                                <div className="d-flex align-items-center">
                                    {hasEventPassed ? (
                                        <div className="alert alert-secondary" role="alert">
                                            <p className="mb-0 fw-semibold">Cet événement est terminé</p>
                                        </div>
                                    ) : (
                                        <>
                                            {event.bookingUrl && (
                                                <a
                                                    href={event.bookingUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-warning rounded-pill px-4 d-inline-flex align-items-center"
                                                >
                                                    Réserver
                                                    <ChevronRight size={18} className="ms-2" />
                                                </a>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Related Events Section */}
            {relatedEvents.length > 0 && (
                <section className="bg-light py-5">
                    <div className="container">
                        <h2 className="display-6 fw-bold mb-4">
                            {isUsingLinkedEvents ? 'Événements liés' : 'Événements similaires'}
                        </h2>
                        <div className="row row-cols-1 row-cols-md-3 g-4">
                            {relatedEvents.map(relatedEvent => (
                                <div key={relatedEvent.id} className="col">
                                    <div className="card border-0 shadow h-100 hover-translate-up transition">
                                        {relatedEvent.images && relatedEvent.images.length > 0 && (
                                            <img
                                                src={API_URL + relatedEvent.images[0]?.url}
                                                alt={relatedEvent.title}
                                                className="card-img-top"
                                                style={{ objectFit: 'cover', maxHeight: '250px' }}
                                            />
                                        )}
                                        <div className="card-body d-flex flex-column">
                                            <div className="d-flex align-items-center text-muted small mb-3">
                                                <Calendar size={16} className="me-2" />
                                                {relatedEvent.newEndDate ? (
                                                    <span>
                                                        du <FormatDate dateString={relatedEvent.newStartDate} format="short" />
                                                        {" "}au{" "}
                                                        <FormatDate dateString={relatedEvent.newEndDate} format="short" />
                                                    </span>
                                                ) : (
                                                    <FormatDate dateString={relatedEvent.newStartDate} format="long" />
                                                )}
                                            </div>
                                            <h3 className="fs-5 fw-bold mb-3">{relatedEvent.title}</h3>
                                            <a
                                                href={`/evenement/${relatedEvent.documentId}`}
                                                className="text-dark fw-medium d-inline-flex align-items-center text-decoration-none mt-auto"
                                            >
                                                Voir les détails
                                                <ChevronRight size={16} className="ms-1" />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default SingleEventPage;